import { FC, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Layout, Data } from '@pcmp/react-coworker-components';

import { LoadingFullScreen } from '@/components/helpers/loading/index';
import {
  CodeV2 as Code,
  useGetV2QrCodesBySlugQuery,
} from '@/redux/apis/codes-api';
import { QRCodeLinearChart } from '@/components/page-widgets/stats';
import { useCtaReportPage } from '@/redux/features/cta/hooks';
import { DetailedCodeHeader } from '@/components/page-widgets/codes/detail/header';
import {
  CodeProvider,
  UserProvider,
} from '@/components/page-widgets/create-code/context';
import Button from '@ingka/button';
import arrowLeftIcon from '@ingka/ssr-icon/paths/arrow-left';
import { useLoading } from '@/components/helpers/loading/context';
import { useGetV1UsersMeQuery } from '@/redux/apis/frontend-api';

const { Container } = Layout;
const { Card } = Data;

const CodeStatsContent: FC<{ codeData: Code | undefined }> = ({ codeData }) => {
  const { t } = useTranslation();
  const triggerReportPage = useCtaReportPage();
  const { loading } = useLoading();
  const { data: userData } = useGetV1UsersMeQuery();

  useEffect(() => {
    triggerReportPage('statsPage');
  }, [triggerReportPage]);

  if (!codeData || !userData) return <LoadingFullScreen />;
  if (loading) return <LoadingFullScreen textI18nKey="label.duplicatingCode" />;

  return (
    <UserProvider user={userData}>
      <CodeProvider code={codeData}>
        <DetailedCodeHeader />
        <div className="pb-[100px]">
          <Container className="pt-4">
            <Button
              iconPosition="leading"
              size="medium"
              ssrIcon={arrowLeftIcon}
              text={t('page.stats.detailedStats')}
              type="tertiary"
              onClick={() => window.history.back()}
            />
            <Card className="mt-4 p-6">
              <QRCodeLinearChart code={codeData} />
            </Card>
          </Container>
        </div>
      </CodeProvider>
    </UserProvider>
  );
};

function StatsPage() {
  const { slug } = useParams<{ slug: string }>();
  const effectiveSlug = slug ?? '';
  const {
    data: codeData,
    isLoading,
    isFetching,
  } = useGetV2QrCodesBySlugQuery({ slug: effectiveSlug });
  const loading = isLoading || isFetching;

  if (loading || !effectiveSlug) return <LoadingFullScreen />;

  return <CodeStatsContent codeData={codeData} />;
}

export default StatsPage;
